import {
  teamNestedSeeAllOrgChildren,
  teamNestedSeeAllSameLevelChildren
} from '../constants/groupTypes';
import { getDerivedGuardiansParentGpId } from '../constants/derivedGuardians';
import {
  buildGroupAncestorChain,
  getChildGroupsForParentGp,
  getDirectChildGroups,
  periodLabel,
  resolveGroupIdForGpId,
  resolveParentGroupId,
  isDerivedGuardiansGroup
} from './orgGroupTree';
import { isOwnerTeamName, SYSTEM_GROUP_NAME } from './systemOwner';

export const MY_GROUPS_TITLE = 'My Groups';

function isConfigFlagTrue(value) {
  return value === true || value === 'true';
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v).trim()).filter(Boolean);
}

function buildNameMatcher(list) {
  const terms = normalizeStringList(list).map((v) => v.toLowerCase());
  if (terms.length === 0) return () => false;
  return (name) => {
    const normalized = String(name || '').toLowerCase();
    return terms.some((term) => normalized.includes(term));
  };
}

function groupIsExcludedByName(group, excludes) {
  const matches = buildNameMatcher(excludes);
  return matches(group?.name);
}

function groupIsExcludedById(group, excludeGroupIds) {
  const ids = normalizeStringList(excludeGroupIds);
  if (ids.length === 0 || !group?.id) return false;
  return ids.includes(String(group.id));
}

/**
 * Group ids to hide (team + descendants). Prefer role_group_id_excludes over names — survives renames.
 */
export function collectExcludedGroupIds(
  allGroups,
  { nameExcludes = [], groupIdExcludes = [] } = {},
  orgGroupId,
  orgGpId
) {
  const list = allGroups || [];
  const excludedIds = new Set(normalizeStringList(groupIdExcludes));

  const nameTerms = normalizeStringList(nameExcludes);
  if (nameTerms.length > 0) {
    list.forEach((g) => {
      if (groupIsExcludedByName(g, nameTerms)) {
        excludedIds.add(String(g.id));
      }
    });
  }

  const expanded = new Set();
  excludedIds.forEach((gid) => {
    expanded.add(String(gid));
    addGroupDescendantsToSet(expanded, gid, list, orgGroupId, orgGpId);
  });
  return expanded;
}

/** Resolve team group ids from name terms at template-apply or setup time (stored as role_group_id_excludes). */
export function resolveGroupIdsByNameTerms(allGroups, orgGroupId, orgGpId, terms = []) {
  const ids = collectExcludedGroupIds(
    allGroups,
    { nameExcludes: terms },
    orgGroupId,
    orgGpId
  );
  return Array.from(ids);
}

export function isGroupInExcludedSubtree(
  groupId,
  allGroups,
  policy,
  orgGroupId,
  orgGpId
) {
  if (!groupId) return false;
  const excluded = collectExcludedGroupIds(allGroups, policy, orgGroupId, orgGpId);
  return excluded.has(String(groupId));
}

export function isGuestRole(currentRole) {
  return /guest/i.test(String(currentRole?.group_name || '').trim());
}

export function isSystemOrSuperAdmin(availableUgp, userGroupsPeriods) {
  const hasSystemOrg =
    Array.isArray(availableUgp) &&
    availableUgp.some(
      (ugp) =>
        ugp.parent_group_name === 'System' ||
        String(ugp.parent_group_type || '').toLowerCase() === 'system'
    );
  if (hasSystemOrg) return true;
  const now = new Date();
  return (
    Array.isArray(userGroupsPeriods) &&
    userGroupsPeriods.some((gp) => {
      const startDate = gp.start_date ? new Date(gp.start_date) : new Date(gp.period_start_date);
      const endDate = gp.end_date ? new Date(gp.end_date) : new Date(gp.period_end_date);
      const isCurrent = now >= startDate && now <= endDate;
      const isAdminGroup = typeof gp?.group_name === 'string' && gp.group_name.trim() === 'Super - Admin';
      return isCurrent && isAdminGroup;
    })
  );
}

export function canBrowseAllOrganizations(availableUgp, userGroupsPeriods, currentRole) {
  return isSystemOrSuperAdmin(availableUgp, userGroupsPeriods) && !isGuestRole(currentRole);
}

export function isSystemOrganizationContext(currentRole) {
  return (
    String(currentRole?.parent_group_name || '').trim() === 'System' ||
    String(currentRole?.parent_group_type || '').toLowerCase() === 'system'
  );
}

/** Platform / System org enrollment — may reveal org Owner bridge (not org Super Admin). */
export function viewerIsSystemOperator({
  platformOwnerViewer = false,
  availableUgp = [],
  currentRole = null
} = {}) {
  if (platformOwnerViewer) return true;
  if (isSystemOrganizationContext(currentRole)) return true;
  return (
    Array.isArray(availableUgp) &&
    availableUgp.some((ugp) =>
      parentGroupIsSystem(ugp.parent_group_type, ugp.parent_group_name)
    )
  );
}

export function viewerIsEnrolledOrganizationOwner({
  userId = null,
  ownerGroupIds = [],
  availableUgp = [],
  userGroupsPeriods = []
} = {}) {
  if (!userId || !ownerGroupIds?.length) return false;
  const ownerIds = new Set(ownerGroupIds.map(String));
  const rows = [...(availableUgp || []), ...(userGroupsPeriods || [])];
  return rows.some((row) => {
    if (row.user_id != null && String(row.user_id) !== String(userId)) return false;
    const gid = row.group_id;
    const label = String(row.group_name || '').trim();
    return gid && ownerIds.has(String(gid)) && isOwnerTeamName(label);
  });
}

/**
 * May show the Owner crown on the org/system shell. Requires shell visible and an Owner team present.
 */
export function viewerCanRevealOrgOwnerBridge({
  platformOwnerViewer = false,
  userId = null,
  availableUgp = [],
  userGroupsPeriods = [],
  currentRole = null,
  ownerCornerNodes = [],
  organizationDisplayed = false,
  organization = null
} = {}) {
  if (!organizationDisplayed || !ownerCornerNodes?.length) return false;

  if (isSystemOrgGroupRecord(organization)) {
    return Boolean(platformOwnerViewer);
  }

  if (viewerIsSystemOperator({ platformOwnerViewer, availableUgp, currentRole })) {
    return true;
  }
  const ownerGroupIds = ownerCornerNodes.map((n) => n.group?.id).filter(Boolean);
  return viewerIsEnrolledOrganizationOwner({
    userId,
    ownerGroupIds,
    availableUgp,
    userGroupsPeriods
  });
}

export function ownerCrownLabelsForOrganization(organization) {
  if (isSystemOrgGroupRecord(organization)) {
    return {
      ariaLabel: 'Open platform owner team',
      title: 'Platform owner',
      listLabel: 'Platform owner'
    };
  }
  return {
    ariaLabel: 'Open organization owner team',
    title: 'Organization owner',
    listLabel: 'Organization owner'
  };
}

function parentGroupIsSystem(parentType, parentName) {
  const type = String(parentType || '').toLowerCase();
  const name = String(parentName || '').trim();
  return type === 'system' || name.toLowerCase() === SYSTEM_GROUP_NAME.toLowerCase();
}

export function isSystemOrgGroupRecord(group) {
  if (!group) return false;
  const type = String(group.group_type || '').toLowerCase();
  const name = String(group.name || '').trim();
  return type === 'system' || name.toLowerCase() === SYSTEM_GROUP_NAME.toLowerCase();
}

/** Owner team under a customer organization (not System → Owner). */
export function isOrganizationOwnerTeamGroup(group, allGroups, orgGroupId, orgGpId) {
  if (!group || !isOwnerTeamName(group.name)) return false;
  if (isPlatformOwnerTeamGroup(group, allGroups, orgGroupId, orgGpId)) return false;
  const teamType = String(group.group_type || 'team').toLowerCase();
  if (teamType && teamType !== 'team') return false;

  const org = (allGroups || []).find((g) => String(g.id) === String(orgGroupId));
  if (!org || isSystemOrgGroupRecord(org)) return false;

  const parentType = String(group.parent_group_type || '').toLowerCase();
  if (parentType === 'organization' || parentType === 'school') return true;

  const parentName = String(group.parent_group_name || '').trim();
  if (parentName && parentName === String(org.name || '').trim()) return true;

  const gpToGroupId = new Map();
  if (orgGpId && orgGroupId) gpToGroupId.set(String(orgGpId), String(orgGroupId));
  (allGroups || []).forEach((g) => {
    (g.periods || []).forEach((p) => {
      if (p?.id) gpToGroupId.set(String(p.id), String(g.id));
    });
  });
  const parentId = resolveParentGroupId(group, orgGroupId, orgGpId, gpToGroupId);
  if (parentId && String(parentId) === String(orgGroupId)) return true;

  return false;
}

/** Owner team nested under the platform System organization. */
export function isPlatformOwnerTeamGroup(group, allGroups, orgGroupId, orgGpId) {
  if (!group || !isOwnerTeamName(group.name)) return false;
  const teamType = String(group.group_type || 'team').toLowerCase();
  if (teamType && teamType !== 'team') return false;

  if (parentGroupIsSystem(group.parent_group_type, group.parent_group_name)) {
    return true;
  }

  const gpToGroupId = new Map();
  if (orgGpId && orgGroupId) gpToGroupId.set(String(orgGpId), String(orgGroupId));
  (allGroups || []).forEach((g) => {
    (g.periods || []).forEach((p) => {
      if (p?.id) gpToGroupId.set(String(p.id), String(g.id));
    });
  });
  const parentId = resolveParentGroupId(group, orgGroupId, orgGpId, gpToGroupId);
  if (parentId) {
    const parent = (allGroups || []).find((g) => String(g.id) === String(parentId));
    if (isSystemOrgGroupRecord(parent)) return true;
  }

  const org = (allGroups || []).find((g) => String(g.id) === String(orgGroupId));
  return isSystemOrgGroupRecord(org);
}

export function ownerTeamActionBlockedReason({
  platformOwnerViewer = false,
  group = null,
  groupId = null,
  allGroups = [],
  orgGroupId = null,
  orgGpId = null,
  proposedGroupName = null,
  parentGroupId = null
}) {
  if (platformOwnerViewer) return null;

  const target =
    group || (groupId ? (allGroups || []).find((g) => String(g.id) === String(groupId)) : null);

  if (target && isPlatformOwnerTeamGroup(target, allGroups, orgGroupId, orgGpId)) {
    return 'platform';
  }
  if (target && isOrganizationOwnerTeamGroup(target, allGroups, orgGroupId, orgGpId)) {
    return 'org';
  }

  if (proposedGroupName && isOwnerTeamName(proposedGroupName)) {
    const parent = parentGroupId
      ? (allGroups || []).find((g) => String(g.id) === String(parentGroupId))
      : null;
    if (isSystemOrgGroupRecord(parent)) return 'platform';
    const org = (allGroups || []).find((g) => String(g.id) === String(orgGroupId));
    if (parent && isSystemOrgGroupRecord(parent)) return 'platform';
    if (org && !isSystemOrgGroupRecord(org)) return 'org_create';
    if (
      isSystemOrgGroupRecord(org) &&
      (!parentGroupId || String(parentGroupId) === String(orgGroupId))
    ) {
      return 'platform';
    }
    if (orgGroupId && org && !isSystemOrgGroupRecord(org)) return 'org_create';
  }

  return null;
}

export function ownerTeamActionBlockedMessage(reason) {
  if (reason === 'platform') {
    return 'Only the platform owner can modify the platform Owner team.';
  }
  if (reason === 'org' || reason === 'org_create') {
    return 'Organization Owner teams and membership are managed by platform support.';
  }
  return 'You cannot modify this Owner team.';
}

/**
 * Block add-member / create on platform or organization Owner teams for non–platform operators.
 */
export function nonPlatformOwnerBlockedOnOwnerTeam(params) {
  return Boolean(ownerTeamActionBlockedReason(params));
}

/**
 * Build group list entries from GET /api/groups-periods rows (one row per groups_periods).
 */
export function mapGroupsPeriodsRowsToGroups(rows, filterGroupType = null) {
  const groupsMap = new Map();

  (rows || []).forEach((gp) => {
    if (filterGroupType && gp.group_type !== filterGroupType) return;

    if (!groupsMap.has(gp.group_id)) {
      groupsMap.set(gp.group_id, {
        id: gp.group_id,
        name: gp.group_name,
        description: gp.group_description,
        logo_url: gp.logo_url,
        created_at: gp.group_created_at || gp.created_at,
        updated_at: gp.group_updated_at || gp.updated_at,
        group_type: gp.group_type,
        display_label: gp.display_label || null,
        periods: []
      });
    }

    const group = groupsMap.get(gp.group_id);
    group.periods.push({
      id: gp.id,
      period_id: gp.period_id,
      title: gp.title,
      period_title: gp.period_title,
      start_date: gp.start_date,
      end_date: gp.end_date
    });
  });

  const groups = Array.from(groupsMap.values());
  groups.forEach((group) => {
    group.periods.sort((a, b) => {
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      return dateB - dateA;
    });
  });
  return groups;
}

/** Pin platform System group(s) ahead of tenant organizations in My Groups picker. */
export function prependSystemGroupsToOrganizationList(organizationList, systemGroups) {
  if (!Array.isArray(systemGroups) || systemGroups.length === 0) {
    return organizationList;
  }
  const orgIds = new Set((organizationList || []).map((g) => String(g.id)));
  const uniqueSystem = systemGroups.filter((g) => !orgIds.has(String(g.id)));
  return [...uniqueSystem, ...(organizationList || [])];
}

/** @deprecated use resolveTeamScopeParentId */
export const resolveRoleScopeParentId = resolveTeamScopeParentId;

export function findOrganizationInList(schools, currentRole) {
  if (!Array.isArray(schools) || schools.length === 0 || !currentRole) return null;
  const gpId = currentRole.parent_groups_periods_id;
  const name = currentRole.parent_group_name;
  const parentType = String(currentRole.parent_group_type || '').toLowerCase();

  if (gpId) {
    const byGp = schools.find((s) =>
      (s.periods || []).some((p) => String(p.id) === String(gpId))
    );
    if (byGp) return byGp;
  }

  if (parentType === 'system') {
    const byType = schools.find((s) => String(s.group_type || '').toLowerCase() === 'system');
    if (byType) return byType;
  }

  if (name) {
    return schools.find((s) => String(s.name || '').trim() === String(name).trim()) || null;
  }
  return null;
}

export function resolveTeamScopeParentId(availableUgp, userGroupsPeriods, currentRole) {
  const gpId = currentRole?.parent_groups_periods_id;
  if (gpId && !isSystemOrganizationContext(currentRole)) return gpId;
  if (canBrowseAllOrganizations(availableUgp, userGroupsPeriods, currentRole) && isSystemOrganizationContext(currentRole)) {
    return null;
  }
  return gpId ?? null;
}

/**
 * Resolve Groups page action flags from page_configuration (Page Permissions Manager).
 * Legacy `create: true` enables both when the new keys are omitted.
 */
export function findOrganizationByOrgGpId(schools, orgGpId) {
  if (!orgGpId || !Array.isArray(schools)) return null;
  const sid = String(orgGpId);
  return (
    schools.find((s) => (s.periods || []).some((p) => String(p.id) === sid)) || null
  );
}

/**
 * True when the user's team period has team_nested metadata see_all_same_level_children,
 * or page_configuration.see_all_same_level_children is enabled.
 */
export function userHasSameLevelBrowseGrant(
  userGroupsPeriods,
  allGroups,
  orgGpId,
  pageConfiguration = null
) {
  if (isConfigFlagTrue(pageConfiguration?.see_all_same_level_children)) return true;
  const orgGp = String(orgGpId || '');
  for (const row of userGroupsPeriods || []) {
    const ugpGp = String(row.groups_periods_id || row.id || '');
    if (!ugpGp || (orgGp && ugpGp === orgGp)) continue;
    for (const group of allGroups || []) {
      const period = (group.periods || []).find((p) => String(p.id) === ugpGp);
      if (period && teamNestedSeeAllSameLevelChildren(period.hierarchy_link_metadata)) {
        return true;
      }
    }
  }
  return false;
}

/** Team-level enrollments in this org (excludes org gp membership). */
export function collectUserSeedGroupIds(userGroupsPeriods, allGroups, orgGroupId, orgGpId) {
  const gpToGroupId = new Map();
  if (orgGpId && orgGroupId) gpToGroupId.set(String(orgGpId), String(orgGroupId));
  (allGroups || []).forEach((g) => {
    (g.periods || []).forEach((p) => {
      if (p?.id) gpToGroupId.set(String(p.id), String(g.id));
    });
  });

  const orgGp = String(orgGpId || '');
  const seedGroupIds = new Set();
  (userGroupsPeriods || []).forEach((row) => {
    const ugpGp = String(row.groups_periods_id || row.id || '');
    if (!ugpGp || (orgGp && ugpGp === orgGp)) return;
    const gid = gpToGroupId.get(ugpGp);
    if (gid && String(gid) !== String(orgGroupId)) seedGroupIds.add(String(gid));
  });
  return seedGroupIds;
}

function addGroupDescendantsToSet(keep, groupId, allGroups, orgGroupId, orgGpId) {
  const g = (allGroups || []).find((x) => String(x.id) === String(groupId));
  if (!g) return;
  getDirectChildGroups(allGroups, groupId, orgGroupId, orgGpId).forEach((child) => {
    const cid = String(child.id);
    if (!keep.has(cid)) {
      keep.add(cid);
      addGroupDescendantsToSet(keep, cid, allGroups, orgGroupId, orgGpId);
    }
  });
  (g.periods || []).forEach((period) => {
    getChildGroupsForParentGp(allGroups, period.id).forEach((nested) => {
      const nid = String(nested.id);
      if (!keep.has(nid)) {
        keep.add(nid);
        addGroupDescendantsToSet(keep, nid, allGroups, orgGroupId, orgGpId);
      }
    });
  });
}

export function filterGroupsByNamePolicy(
  allGroups,
  { includes = [], excludes = [], excludeGroupIds = [] } = {},
  orgGroupId,
  orgGpId
) {
  if (!Array.isArray(allGroups) || allGroups.length === 0) return [];
  let list = allGroups;

  const includeMatches = buildNameMatcher(includes);
  if (normalizeStringList(includes).length > 0) {
    const keepIds = new Set();
    list.forEach((g) => {
      if (includeMatches(g?.name)) keepIds.add(String(g.id));
    });
    list.forEach((g) => {
      if (keepIds.has(String(g.id))) return;
      const parentGp = String(g.parent_groups_periods_id || '');
      const parentInList = list.find((x) => (x.periods || []).some((p) => String(p.id) === parentGp));
      if (parentInList && keepIds.has(String(parentInList.id))) keepIds.add(String(g.id));
    });
    list = list.filter((g) => keepIds.has(String(g.id)));
  }

  const excludedIds = collectExcludedGroupIds(
    list,
    { nameExcludes: excludes, groupIdExcludes: excludeGroupIds },
    orgGroupId,
    orgGpId
  );
  if (excludedIds.size > 0) {
    list = list.filter((g) => !excludedIds.has(String(g.id)));
  }
  return list;
}

/**
 * All sibling teams at the seed depth (same parent gp), plus every descendant.
 * Does not include ancestors or branches above that level.
 */
export function expandSameLevelPeersAndDescendants(
  seedGroupIds,
  allGroups,
  orgGroupId,
  orgGpId
) {
  if (!Array.isArray(allGroups) || allGroups.length === 0) return [];
  const seeds = seedGroupIds instanceof Set ? seedGroupIds : new Set(seedGroupIds || []);
  if (seeds.size === 0) return allGroups;

  const byId = new Map(allGroups.map((g) => [String(g.id), g]));
  const gpToGroupId = new Map();
  if (orgGpId && orgGroupId) gpToGroupId.set(String(orgGpId), String(orgGroupId));
  allGroups.forEach((g) => {
    (g.periods || []).forEach((p) => {
      if (p?.id) gpToGroupId.set(String(p.id), String(g.id));
    });
  });

  const keep = new Set();
  seeds.forEach((seedId) => {
    const seed = byId.get(String(seedId));
    if (!seed) return;
    const parentGroupId =
      resolveParentGroupId(seed, orgGroupId, orgGpId, gpToGroupId) || String(orgGroupId);
    const peers =
      String(parentGroupId) === String(orgGroupId)
        ? getDirectChildGroups(allGroups, orgGroupId, orgGroupId, orgGpId)
        : getDirectChildGroups(allGroups, parentGroupId, orgGroupId, orgGpId);

    peers.forEach((peer) => {
      keep.add(String(peer.id));
      addGroupDescendantsToSet(keep, peer.id, allGroups, orgGroupId, orgGpId);
    });
  });

  return allGroups.filter((g) => keep.has(String(g.id)));
}

export function isGroupInEnrollmentSubtree(groupId, seedGroupIds, allGroups, orgGroupId, orgGpId) {
  if (!groupId) return false;
  const seeds = seedGroupIds instanceof Set ? seedGroupIds : new Set(seedGroupIds || []);
  if (seeds.size === 0) return false;
  const keep = new Set();
  seeds.forEach((seedId) => {
    keep.add(String(seedId));
    addGroupDescendantsToSet(keep, seedId, allGroups, orgGroupId, orgGpId);
  });
  return keep.has(String(groupId));
}

export function isGroupInSameLevelPeerSubtree(
  groupId,
  seedGroupIds,
  allGroups,
  orgGroupId,
  orgGpId
) {
  if (!groupId) return false;
  const expanded = expandSameLevelPeersAndDescendants(
    seedGroupIds,
    allGroups,
    orgGroupId,
    orgGpId
  );
  return expanded.some((g) => String(g.id) === String(groupId));
}

/**
 * Shared scoping for member-list actions (add member, open profile) on a team group.
 * Legacy configs without scope keys apply the action on any visible group when the action flag is on.
 *
 * Future: per-member or per-grantee deny lists (e.g. profile_open_exclude_ugp_ids) can be
 * evaluated here without changing the Groups members list visibility.
 */
function canPerformMemberActionInGroup({
  configuration,
  groupId,
  userGroupsPeriods,
  allGroups,
  orgGroupId,
  orgGpId,
  platformOwnerViewer = false,
  actionEnabled = false,
  ownGroupAllowed = false,
  sameLevelAllowed = false,
  ownGroupScopeKey = 'add_members_to_own_group',
  sameLevelScopeKey = 'can_add_members_same_level'
}) {
  const cfg = configuration && typeof configuration === 'object' ? configuration : {};
  const { roleGroupNameExcludes, roleGroupIdExcludes } = getGroupsPageCapabilities(configuration);
  if (!actionEnabled || !groupId) return false;
  const target = (allGroups || []).find((g) => String(g.id) === String(groupId));
  if (
    isGroupInExcludedSubtree(groupId, allGroups, {
      nameExcludes: roleGroupNameExcludes,
      groupIdExcludes: roleGroupIdExcludes
    }, orgGroupId, orgGpId)
  ) {
    return false;
  }
  if (
    nonPlatformOwnerBlockedOnOwnerTeam({
      platformOwnerViewer,
      group: target,
      groupId,
      allGroups,
      orgGroupId,
      orgGpId
    })
  ) {
    return false;
  }
  if (target && groupIsExcludedByName(target, roleGroupNameExcludes)) return false;
  if (target && groupIsExcludedById(target, roleGroupIdExcludes)) return false;

  const seedGroupIds = collectUserSeedGroupIds(
    userGroupsPeriods,
    allGroups,
    orgGroupId,
    orgGpId
  );
  if (seedGroupIds.size === 0) return true;

  const hasMemberScopeConfig =
    cfg[ownGroupScopeKey] != null || cfg[sameLevelScopeKey] != null;

  if (!hasMemberScopeConfig) return true;

  const inOwn = isGroupInEnrollmentSubtree(
    groupId,
    seedGroupIds,
    allGroups,
    orgGroupId,
    orgGpId
  );
  const inSameLevel = isGroupInSameLevelPeerSubtree(
    groupId,
    seedGroupIds,
    allGroups,
    orgGroupId,
    orgGpId
  );

  if (ownGroupAllowed && inOwn) return true;
  if (sameLevelAllowed && inSameLevel) return true;
  return false;
}

/**
 * Whether the user may enroll members into the given team group, given page_configuration scoping.
 * Legacy configs without add-members scope keys keep global can_add_members on any visible group.
 */
export function canAddMembersToGroup(params) {
  const caps = getGroupsPageCapabilities(params.configuration);
  return canPerformMemberActionInGroup({
    ...params,
    actionEnabled: caps.canAddMembers,
    ownGroupAllowed: caps.addMembersToOwnGroup,
    sameLevelAllowed: caps.canAddMembersSameLevel,
    ownGroupScopeKey: 'add_members_to_own_group',
    sameLevelScopeKey: 'can_add_members_same_level'
  });
}

/**
 * Whether member rows link to User Profile. Default off when can_open_member_profiles is unset.
 * Listing members does not require this flag; user-profiles page access is separate.
 */
export function canOpenMemberProfilesInGroup(params) {
  const caps = getGroupsPageCapabilities(params.configuration);
  return canPerformMemberActionInGroup({
    ...params,
    actionEnabled: caps.canOpenMemberProfiles,
    ownGroupAllowed: caps.openMemberProfilesToOwnGroup,
    sameLevelAllowed: caps.canOpenMemberProfilesSameLevel,
    ownGroupScopeKey: 'open_member_profiles_to_own_group',
    sameLevelScopeKey: 'can_open_member_profiles_same_level'
  });
}

/**
 * Whether the member roster is shown for a team group-period.
 * Requires can_view_group_members: true (opt-in), then optional scope keys.
 */
export function canViewGroupMembersInGroup(params) {
  const cfg = params.configuration && typeof params.configuration === 'object' ? params.configuration : {};
  if (!isConfigFlagTrue(cfg.can_view_group_members)) return false;
  const caps = getGroupsPageCapabilities(params.configuration);
  return canPerformMemberActionInGroup({
    ...params,
    actionEnabled: true,
    ownGroupAllowed: caps.viewGroupMembersInOwnGroup,
    sameLevelAllowed: caps.canViewGroupMembersSameLevel,
    ownGroupScopeKey: 'view_group_members_in_own_group',
    sameLevelScopeKey: 'can_view_group_members_same_level'
  });
}

/**
 * Whether member rows include contact details (e.g. email).
 * Requires can_view_member_details: true (opt-in), then optional scope keys.
 */
export function canViewMemberDetailsInGroup(params) {
  const cfg = params.configuration && typeof params.configuration === 'object' ? params.configuration : {};
  if (!isConfigFlagTrue(cfg.can_view_member_details)) return false;
  const caps = getGroupsPageCapabilities(params.configuration);
  return canPerformMemberActionInGroup({
    ...params,
    actionEnabled: true,
    ownGroupAllowed: caps.viewMemberDetailsInOwnGroup,
    sameLevelAllowed: caps.canViewMemberDetailsSameLevel,
    ownGroupScopeKey: 'view_member_details_in_own_group',
    sameLevelScopeKey: 'can_view_member_details_same_level'
  });
}

/**
 * Whether member rows include enrollment/access details (UGP window, override, can_login).
 * Requires can_view_member_access: true (opt-in), then optional scope keys.
 */
export function canViewMemberAccessInGroup(params) {
  const cfg = params.configuration && typeof params.configuration === 'object' ? params.configuration : {};
  if (!isConfigFlagTrue(cfg.can_view_member_access)) return false;
  const caps = getGroupsPageCapabilities(params.configuration);
  return canPerformMemberActionInGroup({
    ...params,
    actionEnabled: true,
    ownGroupAllowed: caps.viewMemberAccessInOwnGroup,
    sameLevelAllowed: caps.canViewMemberAccessSameLevel,
    ownGroupScopeKey: 'view_member_access_in_own_group',
    sameLevelScopeKey: 'can_view_member_access_same_level'
  });
}

/**
 * True when the user's active team period is linked via team_nested with see_all_org_children.
 */
export function userHasTeamNestedOrgBrowseGrant(userGroupsPeriods, allGroups, orgGpId) {
  const orgGp = String(orgGpId || '');
  for (const row of userGroupsPeriods || []) {
    const ugpGp = String(row.groups_periods_id || row.id || '');
    if (!ugpGp || (orgGp && ugpGp === orgGp)) continue;
    for (const group of allGroups || []) {
      const period = (group.periods || []).find((p) => String(p.id) === ugpGp);
      if (period && teamNestedSeeAllOrgChildren(period.hierarchy_link_metadata)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * System / Super Admin: full org tree on My Groups without team_nested metadata.
 * Everyone else: own team subtrees unless team_nested metadata see_all_org_children is set.
 */
export function myGroupsBypassesMembershipFilter(availableUgp, userGroupsPeriods, currentRole) {
  return canBrowseAllOrganizations(availableUgp, userGroupsPeriods, currentRole);
}

/**
 * Limit nested team tree to subtrees the user can reach from their team-level memberships.
 * Org-level membership only (no team ugp in org) returns the full list unchanged.
 * team_nested metadata see_all_org_children on the user's team link grants full org tree.
 */
export function filterNestedGroupsToUserAccess(
  allGroups,
  userGroupsPeriods,
  orgGroupId,
  orgGpId,
  accessContext = null
) {
  if (!Array.isArray(allGroups) || allGroups.length === 0) return [];

  if (
    accessContext &&
    myGroupsBypassesMembershipFilter(
      accessContext.availableUgp,
      accessContext.userGroupsPeriods,
      accessContext.currentRole
    )
  ) {
    return allGroups;
  }

  const gpToGroupId = new Map();
  if (orgGpId && orgGroupId) gpToGroupId.set(String(orgGpId), String(orgGroupId));
  allGroups.forEach((g) => {
    (g.periods || []).forEach((p) => {
      if (p?.id) gpToGroupId.set(String(p.id), String(g.id));
    });
  });

  const orgGp = String(orgGpId || '');
  const seedGroupIds = collectUserSeedGroupIds(
    userGroupsPeriods,
    allGroups,
    orgGroupId,
    orgGpId
  );

  if (seedGroupIds.size === 0) return allGroups;

  const pageConfiguration = accessContext?.pageConfiguration || null;

  if (userHasTeamNestedOrgBrowseGrant(userGroupsPeriods, allGroups, orgGpId)) {
    return allGroups;
  }

  if (userHasSameLevelBrowseGrant(userGroupsPeriods, allGroups, orgGpId, pageConfiguration)) {
    return expandSameLevelPeersAndDescendants(
      seedGroupIds,
      allGroups,
      orgGroupId,
      orgGpId
    );
  }

  const byId = new Map(allGroups.map((g) => [String(g.id), g]));
  const keep = new Set();

  const addAncestors = (groupId) => {
    let g = byId.get(String(groupId));
    const guard = new Set();
    while (g && !guard.has(String(g.id))) {
      guard.add(String(g.id));
      keep.add(String(g.id));
      const parentGp = String(g.parent_groups_periods_id || '');
      let parentGroup = null;
      if (orgGp && parentGp === orgGp && orgGroupId) {
        parentGroup = byId.get(String(orgGroupId));
      } else {
        parentGroup =
          allGroups.find((x) => (x.periods || []).some((p) => String(p.id) === parentGp)) ||
          (g.parent_group_id ? byId.get(String(g.parent_group_id)) : null);
      }
      if (!parentGroup || String(parentGroup.id) === String(g.id)) break;
      if (orgGroupId && String(parentGroup.id) === String(orgGroupId)) {
        keep.add(String(orgGroupId));
        break;
      }
      g = parentGroup;
    }
  };

  const addDescendants = (groupId) => {
    const g = byId.get(String(groupId));
    if (!g) return;
    getDirectChildGroups(allGroups, groupId, orgGroupId, orgGpId).forEach((child) => {
      const cid = String(child.id);
      if (!keep.has(cid)) {
        keep.add(cid);
        addDescendants(cid);
      }
    });
    (g.periods || []).forEach((period) => {
      getChildGroupsForParentGp(allGroups, period.id).forEach((nested) => {
        const nid = String(nested.id);
        if (!keep.has(nid)) {
          keep.add(nid);
          addDescendants(nid);
        }
      });
    });
  };

  seedGroupIds.forEach((gid) => {
    addAncestors(gid);
    keep.add(String(gid));
    addDescendants(gid);
  });

  return allGroups.filter((g) => keep.has(String(g.id)));
}

export function getGroupsPageCapabilities(configuration) {
  const cfg = configuration && typeof configuration === 'object' ? configuration : {};
  const canCreateEntities =
    isConfigFlagTrue(cfg.can_create_entities);
  const canAddMembers = isConfigFlagTrue(cfg.can_add_members);
  const addMembersToOwnGroup =
    canAddMembers && isConfigFlagTrue(cfg.add_members_to_own_group);
  const canAddMembersSameLevel = isConfigFlagTrue(cfg.can_add_members_same_level);
  const canOpenMemberProfiles = isConfigFlagTrue(cfg.can_open_member_profiles);
  const openMemberProfilesToOwnGroup =
    canOpenMemberProfiles && isConfigFlagTrue(cfg.open_member_profiles_to_own_group);
  const canOpenMemberProfilesSameLevel = isConfigFlagTrue(
    cfg.can_open_member_profiles_same_level
  );
  const canViewGroupMembers = isConfigFlagTrue(cfg.can_view_group_members);
  const viewGroupMembersInOwnGroup =
    canViewGroupMembers && isConfigFlagTrue(cfg.view_group_members_in_own_group);
  const canViewGroupMembersSameLevel = isConfigFlagTrue(cfg.can_view_group_members_same_level);
  const canViewMemberDetails = isConfigFlagTrue(cfg.can_view_member_details);
  const viewMemberDetailsInOwnGroup =
    canViewMemberDetails && isConfigFlagTrue(cfg.view_member_details_in_own_group);
  const canViewMemberDetailsSameLevel = isConfigFlagTrue(cfg.can_view_member_details_same_level);
  const canViewMemberAccess = isConfigFlagTrue(cfg.can_view_member_access);
  const viewMemberAccessInOwnGroup =
    canViewMemberAccess && isConfigFlagTrue(cfg.view_member_access_in_own_group);
  const canViewMemberAccessSameLevel = isConfigFlagTrue(cfg.can_view_member_access_same_level);
  const canViewDerivedGroups = isConfigFlagTrue(cfg.can_view_derived_groups);
  const seeAllSameLevelChildren = isConfigFlagTrue(cfg.see_all_same_level_children);
  const canCreateOrganizations =
    canCreateEntities && isConfigFlagTrue(cfg.can_create_organizations);
  const createEntitiesInOwnGroup =
    canCreateEntities && isConfigFlagTrue(cfg.create_entities_in_own_group);
  const createEntitiesSameLevel =
    canCreateEntities && isConfigFlagTrue(cfg.create_entities_same_level);
  const createEntitiesOrgWide =
    canCreateEntities && isConfigFlagTrue(cfg.create_entities_org_wide);
  const roleGroupNameIncludes = normalizeStringList(cfg.role_group_name_includes);
  const roleGroupNameExcludes = normalizeStringList(cfg.role_group_name_excludes);
  const roleGroupIdExcludes = normalizeStringList(cfg.role_group_id_excludes);
  return {
    canCreateEntities,
    canAddMembers,
    addMembersToOwnGroup,
    canAddMembersSameLevel,
    canOpenMemberProfiles,
    openMemberProfilesToOwnGroup,
    canOpenMemberProfilesSameLevel,
    canViewGroupMembers,
    viewGroupMembersInOwnGroup,
    canViewGroupMembersSameLevel,
    canViewMemberDetails,
    viewMemberDetailsInOwnGroup,
    canViewMemberDetailsSameLevel,
    canViewMemberAccess,
    viewMemberAccessInOwnGroup,
    canViewMemberAccessSameLevel,
    canViewDerivedGroups,
    seeAllSameLevelChildren,
    canCreateOrganizations,
    createEntitiesInOwnGroup,
    createEntitiesSameLevel,
    createEntitiesOrgWide,
    roleGroupNameIncludes,
    roleGroupNameExcludes,
    roleGroupIdExcludes
  };
}

export function canCreateInGroupContext({
  configuration,
  targetGroupId = null,
  parentGroupId = null,
  userGroupsPeriods,
  allGroups,
  orgGroupId,
  orgGpId,
  availableUgp,
  currentRole,
  platformOwnerViewer = false,
  proposedGroupName = null
}) {
  const {
    canCreateEntities,
    createEntitiesInOwnGroup,
    createEntitiesSameLevel,
    createEntitiesOrgWide,
    roleGroupNameExcludes,
    roleGroupIdExcludes
  } = getGroupsPageCapabilities(configuration);
  const excludePolicy = {
    nameExcludes: roleGroupNameExcludes,
    groupIdExcludes: roleGroupIdExcludes
  };
  if (!canCreateEntities) return false;
  const target = (allGroups || []).find((g) => String(g.id) === String(targetGroupId));
  const parentTarget = (allGroups || []).find((g) => String(g.id) === String(parentGroupId));
  if (
    nonPlatformOwnerBlockedOnOwnerTeam({
      platformOwnerViewer,
      group: target,
      groupId: targetGroupId,
      allGroups,
      orgGroupId,
      orgGpId,
      proposedGroupName,
      parentGroupId
    }) ||
    nonPlatformOwnerBlockedOnOwnerTeam({
      platformOwnerViewer,
      group: parentTarget,
      groupId: parentGroupId,
      allGroups,
      orgGroupId,
      orgGpId
    })
  ) {
    return false;
  }
  if (target && isGroupInExcludedSubtree(target.id, allGroups, excludePolicy, orgGroupId, orgGpId)) {
    return false;
  }
  if (
    parentTarget &&
    isGroupInExcludedSubtree(parentTarget.id, allGroups, excludePolicy, orgGroupId, orgGpId)
  ) {
    return false;
  }

  if (createEntitiesOrgWide && canBrowseAllOrganizations(availableUgp, userGroupsPeriods, currentRole)) {
    return true;
  }

  const seedGroupIds = collectUserSeedGroupIds(
    userGroupsPeriods,
    allGroups,
    orgGroupId,
    orgGpId
  );
  if (seedGroupIds.size === 0) return false;

  const effectiveGroupId = parentGroupId || targetGroupId;
  if (!effectiveGroupId) return false;

  const gpToGroupId = new Map();
  if (orgGpId && orgGroupId) gpToGroupId.set(String(orgGpId), String(orgGroupId));
  (allGroups || []).forEach((g) => {
    (g.periods || []).forEach((p) => {
      if (p?.id) gpToGroupId.set(String(p.id), String(g.id));
    });
  });
  const seedParentGroupIds = new Set();
  seedGroupIds.forEach((seedId) => {
    const seed = (allGroups || []).find((g) => String(g.id) === String(seedId));
    if (!seed) return;
    const parentId = resolveParentGroupId(seed, orgGroupId, orgGpId, gpToGroupId);
    if (parentId) seedParentGroupIds.add(String(parentId));
  });

  if (
    createEntitiesInOwnGroup &&
    isGroupInEnrollmentSubtree(effectiveGroupId, seedGroupIds, allGroups, orgGroupId, orgGpId)
  ) {
    return true;
  }
  if (
    createEntitiesSameLevel &&
    (
      isGroupInSameLevelPeerSubtree(effectiveGroupId, seedGroupIds, allGroups, orgGroupId, orgGpId) ||
      seedParentGroupIds.has(String(effectiveGroupId))
    )
  ) {
    return true;
  }
  return false;
}

/** Subtitle under page title when viewing a team/group (description only; name is the title). */
export function getGroupPageSubtitle(group) {
  if (!group) return '';
  return String(group.description || '').trim();
}

/**
 * Breadcrumb items for org → nested groups → period (members).
 * Last segment is always non-clickable (current location).
 */
export function buildGroupsPageBreadcrumbs({
  selectedSchool,
  relatedGroups = [],
  groupsBrowseFocus = null,
  selectedRelatedGroup = null,
  selectedRelatedPeriodId = null,
  showAllOrganizationsLink = false,
  allOrganizationsLabel = 'All organizations',
  onGoToOrganizations,
  onGoToOrgTop,
  onFocusGroup
}) {
  const items = [];
  if (showAllOrganizationsLink && onGoToOrganizations) {
    items.push({
      key: 'all-orgs',
      label: allOrganizationsLabel,
      onClick: onGoToOrganizations
    });
  }
  if (!selectedSchool) return items;

  const targetGroup = selectedRelatedGroup || groupsBrowseFocus?.group;
  const hasNestedContext = Boolean(targetGroup);

  items.push({
    key: 'school',
    label: selectedSchool.name,
    onClick: hasNestedContext && onGoToOrgTop ? onGoToOrgTop : undefined
  });

  if (!targetGroup?.id) return items;

  if (isDerivedGuardiansGroup(targetGroup)) {
    const parentGpId = getDerivedGuardiansParentGpId(targetGroup);
    const parentGroupId = resolveGroupIdForGpId(
      relatedGroups,
      selectedSchool.id,
      null,
      parentGpId
    );
    if (parentGroupId && String(parentGroupId) !== String(selectedSchool.id)) {
      const chain = buildGroupAncestorChain(
        relatedGroups,
        selectedSchool.id,
        parentGroupId
      );
      chain.forEach((group) => {
        items.push({
          key: `group-${group.id}`,
          label: group.name,
          onClick: onFocusGroup ? () => onFocusGroup(group) : undefined
        });
      });
    }
    items.push({
      key: 'derived-guardians',
      label: targetGroup.name || 'Guardians'
    });
    return items;
  }

  const chain = buildGroupAncestorChain(relatedGroups, selectedSchool.id, targetGroup.id);
  chain.forEach((group, index) => {
    const isLastGroup = index === chain.length - 1;
    const activePeriod =
      selectedRelatedGroup && isLastGroup && selectedRelatedPeriodId
        ? (group.periods || []).find((p) => String(p.id) === String(selectedRelatedPeriodId))
        : null;

    if (activePeriod) {
      items.push({
        key: `group-${group.id}`,
        label: group.name,
        onClick: onFocusGroup ? () => onFocusGroup(group) : undefined
      });
      items.push({
        key: `period-${activePeriod.id}`,
        label: periodLabel(activePeriod)
      });
      return;
    }

    if (isLastGroup) {
      items.push({
        key: `group-${group.id}`,
        label: group.name
      });
      return;
    }

    items.push({
      key: `group-${group.id}`,
      label: group.name,
      onClick: onFocusGroup ? () => onFocusGroup(group) : undefined
    });
  });

  return items;
}
